'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["isReplaceInput"],
  _excluded2 = ["isOpen"],
  _excluded3 = ["disabled"];
import I18n from 'I18n';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { DownIcon, RemoveIcon, RightIcon, UpIcon } from 'foundations-assets';
import { Button } from 'foundations-components/transitional/components/Button';
import { HR } from 'foundations-components/transitional/components/HR';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { FieldsetContextProvider } from 'foundations-components/transitional/context';
import styled from 'foundations-theming/utils';
import { useEffect, useRef, useState } from 'react';
import { KeyTypes } from '../../constants/popover';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FindReplaceContainer = styled.div.withConfig({
  displayName: "FindReplace__FindReplaceContainer",
  componentId: "pt4ad1-0"
})(({
  theme
}) => ({
  backgroundColor: theme.color['fill-field-default'],
  padding: theme.space['100'],
  border: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
  borderRadius: theme.borderRadius['100'],
  boxShadow: theme.boxShadow['400']
}));
const FindReplaceInput = props => {
  const {
      isReplaceInput
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(FieldsetContextProvider, {
    size: "small",
    children: /*#__PURE__*/_jsx(TextInput, Object.assign({
      styled: false,
      paddingBlock: "0",
      paddingInline: "100",
      marginInlineStart: isReplaceInput ? '500' : '0',
      inlineSize: "140px",
      blockSize: "30px"
    }, rest))
  });
};
const ToggleReplaceButtonWrapper = styled.div.withConfig({
  displayName: "FindReplace__ToggleReplaceButtonWrapper",
  componentId: "pt4ad1-1"
})(({
  $isOpen
}) => ({
  transform: `rotate(${$isOpen ? '90deg' : '0'})`,
  transitionProperty: 'transform',
  transitionDuration: '300ms',
  transitionTimingFunction: 'ease'
}));
const ToggleReplaceButton = props => {
  const {
      isOpen
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  return /*#__PURE__*/_jsx(ToggleReplaceButtonWrapper, {
    $isOpen: isOpen,
    children: /*#__PURE__*/_jsx(IconButton, Object.assign({
      use: "unstyled",
      size: "xs",
      marginBlock: "0",
      marginInline: "100"
    }, rest, {
      children: /*#__PURE__*/_jsx(RightIcon, {
        color: 'fill-info-default'
      })
    }))
  });
};
const MatchCount = styled.div.withConfig({
  displayName: "FindReplace__MatchCount",
  componentId: "pt4ad1-2"
})(({
  theme
}) => ({
  color: theme.color['text-core-disabled'],
  width: '68px',
  textAlign: 'left',
  fontSize: theme.typography['body-100'].fontSize,
  height: `22px`,
  margin: `0 ${theme.space['50']}`
}));
const ActionButtonWrapper = styled.div.withConfig({
  displayName: "FindReplace__ActionButtonWrapper",
  componentId: "pt4ad1-3"
})(({
  $disabled,
  theme
}) => ({
  borderRadius: theme.borderRadius['full'],
  '&:hover': {
    backgroundColor: !$disabled ? theme.color['border-core-subtle'] : 'transparent'
  }
}));
const ActionButton = props => {
  const {
      disabled
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded3);
  return /*#__PURE__*/_jsx(ActionButtonWrapper, {
    $disabled: disabled,
    children: /*#__PURE__*/_jsx(IconButton, Object.assign({
      use: "transparent",
      size: "xs",
      disabled: disabled
    }, rest))
  });
};
const VerticalDivider = styled.div.withConfig({
  displayName: "FindReplace__VerticalDivider",
  componentId: "pt4ad1-4"
})(({
  theme
}) => ({
  borderLeft: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
  height: `26px`,
  marginLeft: theme.space['200'],
  marginRight: theme.space['200']
}));
const HorizontalDivider = () => /*#__PURE__*/_jsx(HR, {
  marginBlock: "100",
  marginInlineStart: "500",
  marginInlineEnd: "0",
  inlineSize: "calc(100% - 20px)"
});
export default function FindReplace(props) {
  const {
    open,
    searchIndex,
    searchCount,
    findValue
  } = props;
  const [showReplace, setShowReplace] = useState(false);
  const [replaceValue, setReplaceValue] = useState('');
  const findInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  useEffect(() => {
    if (!open) {
      if (showReplace) setShowReplace(false);
      return;
    }
    // Focus on the proper text input when opened
    if (findInputRef.current && !showReplace) {
      findInputRef.current.focus({
        preventScroll: true
      });
    } else if (replaceInputRef.current && showReplace) {
      replaceInputRef.current.focus({
        preventScroll: true
      });
    }
  }, [open, showReplace]);
  const toggleShowReplace = () => {
    setShowReplace(!showReplace);
    if (props.onToggleShowReplace) props.onToggleShowReplace(!showReplace);
  };
  const handleFindInputChange = ({
    target: {
      value
    }
  }) => {
    props.onFindInputChange(value);
  };
  const handleFindNavigation = evt => {
    if (evt.key === KeyTypes.ENTER) {
      if (evt.shiftKey) {
        props.onFindPrev();
      } else {
        props.onFindNext();
      }
    }
  };
  const handleReplaceInputChange = ({
    target: {
      value
    }
  }) => {
    setReplaceValue(value);
  };
  const handleReplaceAllClick = () => {
    props.onReplaceAll(replaceValue);
  };
  const handleClose = ({
    key
  }) => {
    if (key === KeyTypes.ESCAPE) {
      props.onClose();
    }
  };
  const matchCount = `${searchCount < 10000 ? searchIndex : '- '}/${searchCount}`;
  const findPrevDisabled = searchIndex <= 1;
  const findNextDisabled = searchIndex >= searchCount;
  return /*#__PURE__*/_jsxs(FindReplaceContainer, {
    "data-test-id": "find-replace",
    onKeyDown: handleClose,
    children: [/*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      children: [/*#__PURE__*/_jsx(ToggleReplaceButton, {
        "data-test-id": "show-replace-toggle-button",
        onClick: toggleShowReplace,
        isOpen: showReplace
      }), /*#__PURE__*/_jsx(FindReplaceInput, {
        "data-test-id": "find-input",
        size: "small",
        type: "search",
        placeholder: I18n.text('RichTextLib.findreplace.findPlaceholder'),
        inputRef: findInputRef,
        value: findValue,
        onChange: handleFindInputChange,
        onKeyDown: handleFindNavigation
      }), /*#__PURE__*/_jsx(VerticalDivider, {}), /*#__PURE__*/_jsx(MatchCount, {
        "data-test-id": "find-match-count",
        children: matchCount
      }), /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        justify: "end",
        children: [/*#__PURE__*/_jsx(ActionButton, {
          "data-test-id": "find-prev-button",
          disabled: findPrevDisabled,
          onClick: props.onFindPrev,
          children: /*#__PURE__*/_jsx(UpIcon, {
            color: findPrevDisabled ? 'icon-tertiary-disabled' : 'icon-tertiary-default'
          })
        }, "find-prev"), /*#__PURE__*/_jsx(ActionButton, {
          "data-test-id": "find-next-button",
          disabled: findNextDisabled,
          onClick: props.onFindNext,
          children: /*#__PURE__*/_jsx(DownIcon, {
            color: findNextDisabled ? 'icon-tertiary-disabled' : 'icon-tertiary-default'
          })
        }, "find-next"), /*#__PURE__*/_jsx(ActionButton, {
          "data-test-id": "find-replace-close-button",
          onClick: props.onClose,
          children: /*#__PURE__*/_jsx(RemoveIcon, {
            color: "icon-tertiary-default"
          })
        }, "close-find-replace")]
      })]
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: showReplace,
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(HorizontalDivider, {}), /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          children: [/*#__PURE__*/_jsx(FindReplaceInput, {
            "data-test-id": "replace-input",
            size: "small",
            type: "search",
            isReplaceInput: true,
            placeholder: I18n.text('RichTextLib.findreplace.replacePlaceholder'),
            inputRef: replaceInputRef,
            value: replaceValue,
            onChange: handleReplaceInputChange
          }), /*#__PURE__*/_jsx(VerticalDivider, {}), /*#__PURE__*/_jsx(Button, {
            "data-test-id": "replace-all-button",
            use: "tertiary",
            size: "xs",
            disabled: searchCount === 0,
            onClick: handleReplaceAllClick,
            minInlineSize: "144px",
            marginInlineStart: "100",
            children: I18n.text('RichTextLib.findreplace.replaceAllText')
          })]
        })]
      })
    })]
  });
}